import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Eye, MessageSquareText, Search, Trash2 } from "lucide-react";
import "../../assets/Styles/User.css";
import "../../assets/Styles/CustomerService.css";
import Pagination from "../../components/common/pagination";
import Loader from "../../Loader/Loader";
import {
  customerservicelist,
  deletecustomerservice,
  serviceaction,
} from "../../services/Mainservice";
import ReplyPopup from "../../components/common/Reply";
import DeletePopup from "../../components/common/Aresure";
import { Snackbar } from "../../utils/snackbar";

const defaultPagination = {
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  pageSize: 10,
};

const Customerservice = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(defaultPagination);
  const [counts, setCounts] = useState({
    all: 0,
    notReplied: 0,
    unanswered: 0,
    replied: 0,
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [notice, setNotice] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showNotice = useCallback((message, severity = "success") => {
    setNotice({ open: true, message, severity });
  }, []);

  const handleError = useCallback(
    (error) => {
      if (error?.status === 401 || error?.code === 401) {
        localStorage.clear();
        navigate("/");
        return;
      }
      showNotice(error?.message || "Something went wrong", "error");
    },
    [navigate, showNotice],
  );

  const getData = useCallback(
    async (requestedPage = page, showLoader = true) => {
      try {
        if (showLoader) setLoading(true);
        const response = await customerservicelist({
          page: requestedPage,
          pageSize: 10,
          search: search.trim(),
          filter,
          sortBy: "createdAt",
          sortOrder,
        });
        const result = response.data;
        setData(result.data || []);
        setPagination(result.pagination || defaultPagination);
        setCounts(result.counts || {});
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    },
    [filter, handleError, page, search, sortOrder],
  );

  useEffect(() => {
    const timer = setTimeout(() => getData(page), search ? 400 : 0);
    return () => clearTimeout(timer);
  }, [getData, page, search]);

  useEffect(() => {
    if (!notice.open) return undefined;
    const timer = setTimeout(
      () => setNotice((value) => ({ ...value, open: false })),
      4000,
    );
    return () => clearTimeout(timer);
  }, [notice.open]);

  const selectFilter = (value) => {
    setFilter(value);
    setPage(1);
  };

  const handleReply = async (reply) => {
    try {
      setSubmitting(true);
      const response = await serviceaction(selectedTicket.id, reply);
      setReplyOpen(false);
      showNotice(response.data.message || "Reply sent successfully");
      await getData(page, false);
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSubmitting(true);
      const response = await deletecustomerservice(selectedTicket.id);
      setDeleteOpen(false);
      showNotice(response.data.message || "Ticket deleted successfully");
      const nextPage = data.length === 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) setPage(nextPage);
      else await getData(nextPage, false);
    } catch (error) {
      handleError(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="container1 customer-service-page">
        <div className="data_head customer-service-head">
          <div>
            <h1 className="data_heading">Customer Service</h1>
            <p className="customer-service-subtitle">
              {counts.unansweredText ||
                `${counts.notReplied || counts.unanswered || 0} unanswered requests`}
            </p>
          </div>
          <div className="customer-service-controls">
            <div className="searchicon">
              <Search size={18} aria-hidden="true" color="white" />
              <input
                type="search"
                placeholder="Search tickets..."
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
                aria-label="Search customer service tickets"
              />
            </div>
            <select
              className="filterselect"
              value={sortOrder}
              onChange={(event) => {
                setSortOrder(event.target.value);
                setPage(1);
              }}
              aria-label="Sort tickets"
            >
              <option value="DESC">Newest first</option>
              <option value="ASC">Oldest first</option>
            </select>
          </div>
        </div>

        <div
          className="customer-service-stats"
          role="group"
          aria-label="Customer service statistics and filters"
        >
          {[
            ["all", "All tickets", counts.all],
            ["not_replied", "Unanswered", counts.notReplied ?? counts.unanswered],
            ["replied", "Replied", counts.replied],
          ].map(([value, label, count]) => (
            <button
              type="button"
              key={value}
              className={filter === value ? "active" : ""}
              onClick={() => selectFilter(value)}
            >
              <span>{label}</span>
              <strong>{count || 0}</strong>
            </button>
          ))}
        </div>

        <div className="data_table_over">
          <table className="data_table customer-service-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Subject</th>
                <th>Message</th>
                <th>Submitted</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => {
                const name =
                  item.FullName || item.FirstName || item.name || "Guest";
                const message = item.Description || item.message || "—";
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="data_userdetails">
                        {item.image || item.user?.image ? (
                          <img
                            src={item.image || item.user.image}
                            className="data_userdetails_img"
                            alt=""
                          />
                        ) : (
                          <span className="customer-avatar" aria-hidden="true">
                            {name.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <div className="customer-cell-copy">
                          <strong>{name}</strong>
                          <small>
                            {item.userEmail || item.email || "No email"}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>{item.subject || "—"}</td>
                    <td>
                      <span className="ticket-message-preview" title={message}>
                        {message}
                      </span>
                    </td>
                    <td>
                      {item.formattedDate ||
                        item.dateTime ||
                        item.createdAt ||
                        "—"}
                    </td>
                    <td>
                      <span
                        className={
                          item.isReply ? "complete_data" : "cancel_data"
                        }
                      >
                        {item.isReply ? "Replied" : "Unanswered"}
                      </span>
                    </td>
                    <td>
                      <div className="user_actionicon customer-actions">
                        <button
                          type="button"
                          title="View details"
                          onClick={() =>
                            navigate(`/Customer_Service/${item.id}`)
                          }
                        >
                          <Eye size={19} />
                        </button>
                        {!item.isReply && (
                          <button
                            type="button"
                            title="Reply"
                            onClick={() => {
                              setSelectedTicket(item);
                              setReplyOpen(true);
                            }}
                          >
                            <MessageSquareText size={19} />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Delete"
                          onClick={() => {
                            setSelectedTicket(item);
                            setDeleteOpen(true);
                          }}
                        >
                          <Trash2 size={19} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && data.length === 0 && (
            <div className="customer-service-empty">
              <MessageSquareText size={34} />
              <p>
                {search
                  ? "No tickets match your search."
                  : "No customer service tickets found."}
              </p>
            </div>
          )}
        </div>
        <Pagination pagedata={pagination} onPageChange={setPage} />
      </div>

      {loading && <Loader />}
      <ReplyPopup
        key={selectedTicket?.id || "reply"}
        isOpen={replyOpen}
        setisOpen={setReplyOpen}
        handleReply={handleReply}
        onClose={() => setReplyOpen(false)}
        ticketId={selectedTicket?.id}
        isSubmitting={submitting}
      />
      <DeletePopup
        isOpen={deleteOpen}
        setisOpen={setDeleteOpen}
        handleDelete={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />
      <Snackbar
        open={notice.open}
        message={notice.message}
        severity={notice.severity}
        onclose={() => setNotice((value) => ({ ...value, open: false }))}
      />
    </>
  );
};

export default Customerservice;
